import { supabase } from './supabase';
import { sendSMS } from './messaging';
import { sendEmail, sendGmailEmail } from './email';
import { createProposalCreatedMilestone, createProposalSentMilestone, createProposalViewedMilestone, createProposalApprovedMilestone } from './bookingMilestones';

export interface ProposalService {
  id: string;
  proposal_id: string;
  offer_id: string;
  service_id: string;
  vehicle_id: string;
  name: string;
  description: string | null;
  price: number;
  tax_exempt: boolean;
  tax_rate: number | null;
  tax_amount: number;
  discount_amount: number;
  total_amount: number;
  duration_minutes: number;
  order_index: number;
  created_at: string;
  updated_at: string;
  vehicle_make: string;
  vehicle_model: string;
  vehicle_year: string;
  vehicle_color: string;
  vehicle_vin: string;
  vehicle_license_plate: string;
  vehicle_is_primary: boolean;
  isSelectable?: boolean;
}

export interface ProposalOffer {
  id: string;
  proposal_id: string;
  title: string;
  status: string;
  order_index: number;
  created_at: string;
  updated_at: string;
  services?: ProposalService[];
}

export interface Proposal {
  id: string;
  business_id: string;
  customer_id: string;
  title: string;
  status: string;
  valid_until: string | null;
  notes: string | null;
  subtotal: number;
  discount_amount: number;
  tax_amount: number;
  total_amount: number;
  created_at: string;
  updated_at: string;
  customer?: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
  };
  offers?: ProposalOffer[];
}

interface CreateProposalParams {
  business_id: string;
  customer_id: string;
  title: string;
  valid_until?: string;
  expiry_date?: string;
  notes?: string;
  offers: {
    title: string;
    services: {
      service_id: string;
      vehicle_id: string;
      name: string;
      description?: string;
      price: number;
      tax_exempt?: boolean;
      tax_rate?: number;
      discount_amount?: number;
      duration_minutes: number;
      isSelectable?: boolean;
    }[];
  }[];
}

interface CreateProposalServiceParams {
  service_id: string;
  vehicle_id: string;
  name: string;
  description?: string;
  price: number;
  tax_exempt?: boolean;
  tax_rate?: number;
  discount_amount?: number;
  duration_minutes: number;
  isSelectable?: boolean;
}

export async function getProposals(businessId: string, options?: { limit?: number; offset?: number }) {
  const { limit = 30, offset = 0 } = options || {};

  const { data, error } = await supabase
    .from('proposals')
    .select(`
      *,
      customer:customers(
        first_name,
        last_name,
        email,
        phone
      ),
      offers:proposal_offers(
        *,
        services:proposal_services_with_details(*)
      )
    `)
    .eq('business_id', businessId)
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) throw error;
  return data as Proposal[];
}

export async function getProposalsByCustomer(businessId: string, customerId: string, options?: { limit?: number; offset?: number }) {
  if (!businessId || typeof businessId !== 'string') {
    throw new Error('Valid business ID is required');
  }
  
  if (!customerId || typeof customerId !== 'string') {
    throw new Error('Valid customer ID is required');
  }

  const { limit = 50, offset = 0 } = options || {};

  console.log('Fetching proposals for customer:', customerId, { businessId, limit, offset });
  
  const { data, error } = await supabase
    .from('proposals')
    .select(`
      *,
      customer:customers(
        first_name,
        last_name,
        email,
        phone
      ),
      offers:proposal_offers(
        *,
        services:proposal_services_with_details(*)
      )
    `)
    .eq('business_id', businessId)
    .eq('customer_id', customerId)
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) {
    console.error('Error fetching proposals for customer:', error);
    throw error;
  }

  return data as Proposal[];
}

// Get total count of proposals for pagination
export async function getProposalsCount(businessId: string): Promise<number> {
  const { count, error } = await supabase
    .from('proposals')
    .select('*', { count: 'exact', head: true })
    .eq('business_id', businessId);

  if (error) {
    console.error('Error counting proposals:', error);
    throw error;
  }

  return count || 0;
}

export async function getProposal(id: string) {
  const { data, error } = await supabase
    .from('proposals')
    .select(`
      *,
      customer:customers(
        first_name,
        last_name,
        email,
        phone
      ),
      offers:proposal_offers(
        *,
        services:proposal_services_with_details(*)
      )
    `)
    .eq('id', id)
    .single();

  if (error) throw error;
  return data as Proposal;
}

export async function createProposal(params: CreateProposalParams) {
  // Create proposal
  const { data: proposalData, error: proposalError } = await supabase
    .from('proposals')
    .insert([{
      business_id: params.business_id,
      customer_id: params.customer_id,
      title: params.title,
      valid_until: params.expiry_date || params.valid_until,
      notes: params.notes,
      status: 'draft'
    }])
    .select()
    .single();

  if (proposalError) throw proposalError;

  // Create offers and services
  for (const [offerIndex, offer] of params.offers.entries()) {
    // Create offer
    const { data: offerData, error: offerError } = await supabase
      .from('proposal_offers')
      .insert([{
        proposal_id: proposalData.id,
        title: offer.title,
        order_index: offerIndex
      }])
      .select()
      .single();

    if (offerError) throw offerError;

    // Create services for this offer
    const { error: servicesError } = await supabase
      .from('proposal_services')
      .insert(
        offer.services.map((service, serviceIndex) => ({
          proposal_id: proposalData.id,
          offer_id: offerData.id,
          service_id: service.service_id,
          vehicle_id: service.vehicle_id,
          name: service.name,
          description: service.description || '',
          price: service.price,
          tax_exempt: service.tax_exempt || false,
          tax_rate: service.tax_rate || 5,
          tax_amount: service.tax_exempt ? 0 : (service.price * (service.tax_rate || 5) / 100),
          discount_amount: service.discount_amount || 0,
          total_amount: service.price - (service.discount_amount || 0) + (service.tax_exempt ? 0 : (service.price * (service.tax_rate || 5) / 100)),
          duration_minutes: service.duration_minutes,
          order_index: serviceIndex,
          is_selectable: service.isSelectable || false,
          is_selected: !(service.isSelectable || false)
        }))
      );

    if (servicesError) throw servicesError;
  }

  // Create proposal milestone for tracking
  try {
    console.log('🎯 Creating proposal milestone:', {
      business_id: params.business_id,
      customer_id: params.customer_id,
      proposal_id: proposalData.id,
      title: params.title
    });
    
    await createProposalCreatedMilestone(
      params.business_id,
      params.customer_id,
      proposalData.id,
      params.title
    );
    
    console.log('✅ Proposal milestone created successfully');
  } catch (milestoneError) {
    console.error('❌ Error creating proposal milestone:', milestoneError);
    console.error('Full error details:', JSON.stringify(milestoneError, null, 2));
    // Don't throw here as the proposal was created successfully
  }

  return getProposal(proposalData.id);
}

async function updateProposal(
  id: string,
  updates: Partial<Proposal> & {
    offers?: {
      id?: string;
      title: string;
      services: {
        id?: string;
        service_id: string;
        vehicle_id: string;
        name: string;
        description?: string;
        price: number;
        tax_exempt?: boolean;
        tax_rate?: number;
        discount_amount?: number;
        duration_minutes: number;
      }[];
    }[];
  }
) {
  const { offers, ...proposalUpdates } = updates;

  // Update proposal
  const { error: proposalError } = await supabase
    .from('proposals')
    .update(proposalUpdates)
    .eq('id', id);

  if (proposalError) throw proposalError;

  // Update offers if provided
  if (offers) {
    // Delete existing offers and services
    const { error: deleteOffersError } = await supabase
      .from('proposal_offers')
      .delete()
      .eq('proposal_id', id);

    if (deleteOffersError) throw deleteOffersError;

    // Create new offers and services
    for (const [offerIndex, offer] of offers.entries()) {
      const { data: offerData, error: offerError } = await supabase
        .from('proposal_offers')
        .insert([{
          proposal_id: id,
          title: offer.title,
          order_index: offerIndex
        }])
        .select()
        .single();

      if (offerError) throw offerError;

      const { error: servicesError } = await supabase
        .from('proposal_services')
        .insert(
          offer.services.map((service, serviceIndex) => ({
            proposal_id: id,
            offer_id: offerData.id,
            service_id: service.service_id,
            vehicle_id: service.vehicle_id,
            name: service.name,
            description: service.description || '',
            price: service.price,
            tax_exempt: service.tax_exempt || false,
            tax_rate: service.tax_rate || 5,
            tax_amount: service.tax_exempt ? 0 : (service.price * (service.tax_rate || 5) / 100),
            discount_amount: service.discount_amount || 0,
            total_amount: service.price - (service.discount_amount || 0) + (service.tax_exempt ? 0 : (service.price * (service.tax_rate || 5) / 100)),
            duration_minutes: service.duration_minutes,
            order_index: serviceIndex,
            is_selectable: service.isSelectable || false,
            is_selected: !(service.isSelectable || false)
          }))
        );

      if (servicesError) throw servicesError;
    }
  }

  return getProposal(id);
}

export async function deleteProposal(id: string) {
  const { error } = await supabase
    .from('proposals')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

/**
 * Gets an existing token for a proposal or creates a new one if none exists
 * UPDATED: Works with new constraint system (unique per proposal)
 */
export async function getOrCreateProposalToken(proposalId: string, businessId: string): Promise<string> {
  if (!supabase) {
    console.error('Supabase client is not initialized');
    throw new Error('Supabase client is not initialized');
  }

  console.log('Getting or creating proposal token for', proposalId);

  // First, get the proposal's expiry date
  const { data: proposalData, error: proposalError } = await supabase!
    .from('proposals')
    .select('valid_until')
    .eq('id', proposalId)
    .single();

  if (proposalError) {
    console.error('Error fetching proposal data:', proposalError);
    throw proposalError;
  }

  if (!proposalData) {
    console.error('No proposal found for id:', proposalId);
    throw new Error('Proposal not found');
  }

  // Check if we already have a token for this proposal (unique constraint)
  const { data: existingTokens, error: queryError } = await supabase!
    .from('public_proposal_tokens')
    .select('token, expires_at')
    .eq('proposal_id', proposalId)
    .limit(1);

  if (queryError) {
    console.error('Error querying for existing tokens:', queryError);
    throw queryError;
  }

  // If we have a token, check if it's still valid
  if (existingTokens && existingTokens.length > 0) {
    const token = existingTokens[0];
    const tokenExpiresAt = new Date(token.expires_at);
    const proposalExpiresAt = proposalData.valid_until ? new Date(proposalData.valid_until) : null;
    const now = new Date();

    // Check if token is still valid (not expired) AND proposal hasn't expired
    const tokenValid = tokenExpiresAt > now;
    const proposalValid = !proposalExpiresAt || proposalExpiresAt > now;

    if (tokenValid && proposalValid) {
      console.log('Using existing token:', token.token);
      return token.token;
    }
    
    // Token exists but is expired - update it with new expiry
    console.log('Updating expired token with new expiry...');
    
    // Calculate new expiry interval
    let expiresIn = '7 days'; // default fallback
    if (proposalData.valid_until) {
      const proposalExpiry = new Date(proposalData.valid_until);
      const diffMs = proposalExpiry.getTime() - now.getTime();
      const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffDays > 0) {
        expiresIn = `${diffDays} days`;
      } else {
        console.log('Proposal already expired, extending token for 1 day for viewing');
        expiresIn = '1 day';
      }
    }

    // Update the existing token's expiry
    const newExpiresAt = new Date();
    const intervalMatch = expiresIn.match(/(\d+)\s*(day|hour|minute)s?/);
    if (intervalMatch) {
      const amount = parseInt(intervalMatch[1]);
      const unit = intervalMatch[2];
      
      switch (unit) {
        case 'day':
          newExpiresAt.setDate(newExpiresAt.getDate() + amount);
          break;
        case 'hour':
          newExpiresAt.setHours(newExpiresAt.getHours() + amount);
          break;
        case 'minute':
          newExpiresAt.setMinutes(newExpiresAt.getMinutes() + amount);
          break;
      }
    }

    const { error: updateError } = await supabase
      .from('public_proposal_tokens')
      .update({ expires_at: newExpiresAt.toISOString() })
      .eq('proposal_id', proposalId);

    if (updateError) {
      console.error('Error updating token expiry:', updateError);
      throw updateError;
    }

    console.log('Token expiry updated successfully');
    return token.token;
  }

  // No token exists, create a new one
  console.log('Creating new proposal token...');
  
  // Calculate expiry interval based on proposal's valid_until date
  let expiresIn = '7 days'; // default fallback
  if (proposalData.valid_until) {
    const proposalExpiry = new Date(proposalData.valid_until);
    const now = new Date();
    const diffMs = proposalExpiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays > 0) {
      expiresIn = `${diffDays} days`;
      console.log(`Setting token expiry to match proposal expiry: ${diffDays} days`);
    } else {
      console.log('Proposal already expired, but creating token with 1 day expiry for viewing');
      expiresIn = '1 day';
    }
  }

  const { data: tokenData, error: tokenError } = await supabase
    .rpc('generate_proposal_token', {
      p_proposal_id: proposalId,
      p_business_id: businessId,
      p_expires_in: expiresIn
    });

  if (tokenError) {
    console.error('Error generating proposal token:', tokenError);
    throw tokenError;
  }

  if (!tokenData) {
    console.error('No token data returned from generate_proposal_token');
    throw new Error('Failed to generate proposal token');
  }

  console.log('Token generated successfully:', tokenData);
  return tokenData;
}

/**
 * Logs a proposal event to the proposal_events table
 */
export async function logProposalEvent(
  proposalId: string,
  businessId: string,
  eventType: string,
  message?: string,
  metadata: Record<string, any> = {}
) {
  try {
    console.log(`Logging proposal event: ${eventType} for proposal ${proposalId}`);
    
    const { data, error } = await supabase.rpc('log_proposal_event', {
      p_proposal_id: proposalId,
      p_business_id: businessId,
      p_event_type: eventType,
      p_message: message || null,
      p_metadata: metadata
    });

    if (error) {
      console.error('Error logging proposal event:', error);
      throw error;
    }

    console.log('Successfully logged proposal event');
    return data;
  } catch (error) {
    console.error('Error in logProposalEvent:', error);
    // Don't throw here to prevent disrupting the main flow
    return null;
  }
}

/**
 * Gets the event history for a proposal
 */
export async function getProposalEvents(proposalId: string) {
  console.log('Fetching events for proposal ID:', proposalId);
  
  try {
    if (!supabase) {
      console.error('Supabase client is not initialized');
      return [];
    }
    
    // Just retrieve the events without trying to join with user data
    const { data, error } = await supabase
      .from('proposal_events')
      .select('*')
      .eq('proposal_id', proposalId)
      .order('event_timestamp', { ascending: false });

    if (error) {
      console.error('Error fetching proposal events:', error);
      throw error;
    }

    console.log('Retrieved proposal events, count:', data?.length, 'events:', data);
    return data || [];
  } catch (error) {
    console.error('Exception in getProposalEvents:', error);
    // Return empty array instead of throwing to avoid breaking the UI
    return [];
  }
}

// Update the sendProposalLink function to log events
export async function sendProposalLink(proposalId: string, businessId: string, customerPhone: string) {
  try {
    if (!supabase) {
      console.error('Supabase client is not initialized');
      throw new Error('Supabase client is not initialized');
    }

    console.log('Starting to send proposal link:', {
      proposalId,
      businessId,
      customerPhone
    });

    // Get or create a token for this proposal
    const tokenData = await getOrCreateProposalToken(proposalId, businessId);

    // Construct the public URL
    const publicUrl = `${window.location.origin}/proposal/${tokenData}`;
    console.log('\n=== PROPOSAL LINK ===');
    console.log(publicUrl);
    console.log('===================\n');

    // Get business name for the message
    let businessName = "your service provider";
    try {
      const { data: business, error: businessError } = await supabase
        .from('businesses')
        .select('name')
        .eq('id', businessId)
        .single();
      
      if (!businessError && business && business.name) {
        businessName = business.name;
      }
    } catch (error) {
      console.error('Error fetching business details:', error);
      // Continue with default business name
    }

    // Send SMS using the existing sendSMS function
    console.log('Sending SMS with proposal link...');
    const result = await sendSMS({
      businessId,
      to: customerPhone,
      message: `You have received a proposal from ${businessName}. View it here: ${publicUrl}`
    });

    if (!result.success) {
      console.error('Error sending SMS:', result.error);
      throw new Error(result.error || 'Failed to send SMS');
    }

    console.log('SMS sent successfully');

    // Update proposal status to 'sent'
    console.log('Updating proposal status to sent...');
    const { error: updateError } = await supabase
      .from('proposals')
      .update({ status: 'sent' })
      .eq('id', proposalId);

    if (updateError) {
      console.error('Error updating proposal status:', updateError);
      throw updateError;
    }

    // Log the proposal sent event
    await logProposalEvent(
      proposalId, 
      businessId, 
      'sent_sms', 
      'Proposal sent via SMS',
      {
        to_phone: customerPhone,
        url: publicUrl
      }
    );

    // Create proposal sent milestone
    try {
      // Get proposal details for milestone
      const { data: proposal, error: proposalError } = await supabase
        .from('proposals')
        .select('title, customer_id')
        .eq('id', proposalId)
        .single();
      
      if (!proposalError && proposal && proposal.customer_id) {
        await createProposalSentMilestone(
          businessId,
          proposal.customer_id,
          proposalId,
          proposal.title,
          'sms',
          customerPhone
        );
        console.log('✅ Proposal sent milestone created successfully');
      } else {
        console.warn('⚠️ Skipping milestone creation - missing customer_id or proposal data:', {
          proposalError,
          proposal,
          customer_id: proposal?.customer_id
        });
      }
    } catch (milestoneError) {
      console.error('❌ Error creating proposal sent milestone:', milestoneError);
      // Don't throw here as the proposal was sent successfully
    }

    console.log('Proposal status updated successfully');
    return { success: true };
  } catch (error) {
    console.error('Error sending proposal link:', error);
    throw error;
  }
}

/**
 * Sends a proposal link via email
 */
export async function sendProposalLinkViaEmail(proposalId: string, businessId: string, customerEmail: string) {
  try {
    if (!supabase) {
      console.error('Supabase client is not initialized');
      throw new Error('Supabase client is not initialized');
    }

    console.log('Starting to send proposal link via email:', {
      proposalId,
      businessId,
      customerEmail
    });

    // Get or create a token for this proposal
    const tokenData = await getOrCreateProposalToken(proposalId, businessId);

    // Construct the public URL
    const publicUrl = `${window.location.origin}/proposal/${tokenData}`;
    console.log('\n=== PROPOSAL LINK (EMAIL) ===');
    console.log(publicUrl);
    console.log('===================\n');
    
    // Get proposal info and business name for better email content
    const { data: proposal, error: proposalError } = await supabase
      .from('proposals')
      .select(`
        title, 
        customer_id,
        business_id,
        businesses:business_id (name)
      `)
      .eq('id', proposalId)
      .single();
    
    if (proposalError) {
      console.error('Error fetching proposal:', proposalError);
      throw proposalError;
    }

    const businessName = proposal.businesses?.name || "your service provider";

    // Send email using our Gmail integration
    console.log('Sending email with proposal link...');
    const emailResult = await sendGmailEmail({
      businessId,
      to: customerEmail,
      subject: `New Proposal: ${proposal.title}`,
      body: `You have received a new proposal from ${businessName}. View it here: ${publicUrl}`
    });

    if (!emailResult.success) {
      console.error('Error sending email:', emailResult.error);
      throw new Error(emailResult.error || 'Failed to send email');
    }

    console.log('Email sent successfully');

    // Update proposal status to 'sent'
    console.log('Updating proposal status to sent...');
    const { error: updateError } = await supabase
      .from('proposals')
      .update({ status: 'sent' })
      .eq('id', proposalId);

    if (updateError) {
      console.error('Error updating proposal status:', updateError);
      throw updateError;
    }

    // Log the proposal sent event
    await logProposalEvent(
      proposalId, 
      businessId, 
      'sent_email', 
      'Proposal sent via email',
      {
        to_email: customerEmail,
        subject: `New Proposal: ${proposal.title}`,
        url: publicUrl
      }
    );

    // Create proposal sent milestone
    try {
      if (proposal.customer_id) {
        await createProposalSentMilestone(
          businessId,
          proposal.customer_id,
          proposalId,
          proposal.title,
          'email',
          customerEmail
        );
        console.log('✅ Proposal sent milestone created successfully');
      } else {
        console.warn('⚠️ Skipping milestone creation - missing customer_id:', proposal);
      }
    } catch (milestoneError) {
      console.error('❌ Error creating proposal sent milestone:', milestoneError);
      // Don't throw here as the proposal was sent successfully
    }

    console.log('Proposal status updated successfully');
    return { success: true };
  } catch (error) {
    console.error('Error sending proposal link via email:', error);
    throw error;
  }
}

/**
 * Sends a proposal link via both email and SMS
 */
export async function sendProposalLinkViaBoth(proposalId: string, businessId: string, customerEmail: string, customerPhone: string) {
  try {
    if (!supabase) {
      console.error('Supabase client is not initialized');
      throw new Error('Supabase client is not initialized');
    }

    console.log('Starting to send proposal link via both methods:', {
      proposalId,
      businessId,
      customerEmail,
      customerPhone
    });

    // Get or create a token for this proposal
    const tokenData = await getOrCreateProposalToken(proposalId, businessId);

    // Construct the public URL to be used for both methods
    const publicUrl = `${window.location.origin}/proposal/${tokenData}`;
    console.log('\n=== PROPOSAL LINK (BOTH) ===');
    console.log(publicUrl);
    console.log('===================\n');

    // Track success of each method
    let emailSuccess = false;
    let smsSuccess = false;
    let errorMessage = '';

    // Get proposal info and business name for email/SMS content
    let proposalTitle = "New Proposal";
    let businessName = "your service provider";
    try {
      const { data: proposal, error: proposalError } = await supabase
        .from('proposals')
        .select(`
          title, 
          businesses:business_id (name)
        `)
        .eq('id', proposalId)
        .single();
      
      if (!proposalError && proposal) {
        proposalTitle = proposal.title;
        if (proposal.businesses?.name) {
          businessName = proposal.businesses.name;
        }
      }
    } catch (error) {
      console.error('Error fetching proposal details:', error);
      // Continue with default title and business name
    }

    // Try to send email
    try {
      // Send email using our Gmail integration
      const emailResult = await sendGmailEmail({
        businessId,
        to: customerEmail,
        subject: `New Proposal: ${proposalTitle}`,
        body: `You have received a new proposal from ${businessName}. View it here: ${publicUrl}`
      });

      if (!emailResult.success) {
        throw new Error(emailResult.error || 'Failed to send email');
      }

      console.log('Email sent successfully');
      emailSuccess = true;
      
      // Log email sent event
      await logProposalEvent(
        proposalId, 
        businessId, 
        'sent_email', 
        'Proposal sent via email',
        {
          to_email: customerEmail,
          subject: `New Proposal: ${proposalTitle}`,
          url: publicUrl
        }
      );
    } catch (emailError) {
      console.error('Error sending proposal via email:', emailError);
      errorMessage += `Email sending failed: ${emailError instanceof Error ? emailError.message : 'Unknown error'}.`;
    }

    // Try to send SMS
    try {
      // Send SMS using the existing sendSMS function
      const smsResult = await sendSMS({
        businessId,
        to: customerPhone,
        message: `You have received a proposal from ${businessName}. View it here: ${publicUrl}`
      });

      if (!smsResult.success) {
        throw new Error(smsResult.error || 'Failed to send SMS');
      }

      console.log('SMS sent successfully');
      smsSuccess = true;
      
      // Log SMS sent event
      await logProposalEvent(
        proposalId, 
        businessId, 
        'sent_sms', 
        'Proposal sent via SMS',
        {
          to_phone: customerPhone,
          url: publicUrl
        }
      );
    } catch (smsError) {
      console.error('Error sending proposal via SMS:', smsError);
      errorMessage += ` SMS sending failed: ${smsError instanceof Error ? smsError.message : 'Unknown error'}.`;
    }

    // If neither method succeeded, throw an error
    if (!emailSuccess && !smsSuccess) {
      throw new Error(`Failed to send proposal: ${errorMessage}`);
    }

    // If at least one method succeeded, update the proposal status
    const { error: updateError } = await supabase
      .from('proposals')
      .update({ status: 'sent' })
      .eq('id', proposalId);

    if (updateError) {
      console.error('Error updating proposal status:', updateError);
      // Don't throw here as we successfully sent at least one message
    } else {
      console.log('Proposal status updated successfully');
    }

    // Create proposal sent milestone
    try {
      // Get proposal details for milestone
      const { data: proposal, error: proposalError } = await supabase
        .from('proposals')
        .select('title, customer_id')
        .eq('id', proposalId)
        .single();
      
      if (!proposalError && proposal && proposal.customer_id) {
        // Determine recipient string
        let recipient = '';
        if (emailSuccess && smsSuccess) {
          recipient = `${customerEmail} and ${customerPhone}`;
        } else if (emailSuccess) {
          recipient = customerEmail;
        } else if (smsSuccess) {
          recipient = customerPhone;
        }

        await createProposalSentMilestone(
          businessId,
          proposal.customer_id,
          proposalId,
          proposal.title,
          'both',
          recipient
        );
        console.log('✅ Proposal sent milestone created successfully');
      } else {
        console.warn('⚠️ Skipping milestone creation - missing customer_id or proposal data:', {
          proposalError,
          proposal,
          customer_id: proposal?.customer_id
        });
      }
    } catch (milestoneError) {
      console.error('❌ Error creating proposal sent milestone:', milestoneError);
      // Don't throw here as the proposal was sent successfully
    }

    // Return partial success information
    return { 
      success: true, 
      emailSent: emailSuccess, 
      smsSent: smsSuccess,
      message: errorMessage || undefined 
    };
  } catch (error) {
    console.error('Error sending proposal via both methods:', error);
    throw error;
  }
}

export async function setProposalServiceSelection(serviceId: string, isSelected: boolean) {
  try {
    if (!supabase) {
      console.error('Supabase client is not initialized');
      throw new Error('Supabase client is not initialized');
    }

    console.log(`Setting service ${serviceId} selection state to ${isSelected}`);

    // Try a direct SQL update first
    const { data: sqlData, error: sqlError } = await supabase.rpc('execute_sql', {
      query: `UPDATE proposal_services SET is_selected = ${isSelected} WHERE id = '${serviceId}' RETURNING *`,
    });

    if (sqlError) {
      console.error('SQL update error:', sqlError);
      
      // Fall back to regular update
      const { data, error } = await supabase
        .from('proposal_services')
        .update({ is_selected: isSelected })
        .eq('id', serviceId)
        .select();

      if (error) {
        console.error('Error updating service selection:', error);
        throw error;
      }

      console.log('Service selection updated successfully via regular update:', data);
      return { success: true, data };
    }

    console.log('Service selection updated successfully via SQL:', sqlData);
    return { success: true, data: sqlData };
  } catch (error) {
    console.error('Error setting service selection:', error);
    throw error;
  }
}

// Update approve/decline to log events
export async function approveProposalOffer(offerId: string) {
  try {
    // First get the proposal ID and details for the offer
    const { data: offerData, error: offerError } = await supabase
      .from('proposal_offers')
      .select('proposal_id, title')
      .eq('id', offerId)
      .single();
    
    if (offerError) throw offerError;
    
    // Call the RPC to approve the offer
    const { error } = await supabase
      .rpc('approve_proposal_offer', { p_offer_id: offerId });

    if (error) throw error;
    
    // Get the proposal details
    const { data: proposalData, error: proposalError } = await supabase
      .from('proposals')
      .select('business_id, customer_id, title')
      .eq('id', offerData.proposal_id)
      .single();
      
    if (proposalError) throw proposalError;
    
    // Log the event
    await logProposalEvent(
      offerData.proposal_id,
      proposalData.business_id,
      'offer_approved',
      `Offer "${offerData.title}" was approved`,
      { offer_id: offerId }
    );

    // Create proposal approved milestone
    try {
      await createProposalApprovedMilestone(
        proposalData.business_id,
        proposalData.customer_id,
        offerData.proposal_id,
        proposalData.title,
        offerData.title
      );
      console.log('✅ Proposal approved milestone created successfully');
    } catch (milestoneError) {
      console.error('❌ Error creating proposal approved milestone:', milestoneError);
      // Don't throw here as the approval was successful
    }
  } catch (error) {
    console.error('Error approving proposal offer:', error);
    throw error;
  }
}

export async function declineProposalOffer(offerId: string) {
  try {
    // First get the proposal ID for the offer
    const { data: offerData, error: offerError } = await supabase
      .from('proposal_offers')
      .select('proposal_id, title')
      .eq('id', offerId)
      .single();
    
    if (offerError) throw offerError;
    
    // Call the RPC to decline the offer
    const { error } = await supabase
      .rpc('decline_proposal_offer', { p_offer_id: offerId });

    if (error) throw error;
    
    // Get the proposal business ID
    const { data: proposalData, error: proposalError } = await supabase
      .from('proposals')
      .select('business_id')
      .eq('id', offerData.proposal_id)
      .single();
      
    if (proposalError) throw proposalError;
    
    // Log the event
    await logProposalEvent(
      offerData.proposal_id,
      proposalData.business_id,
      'offer_declined',
      `Offer "${offerData.title}" was declined`,
      { offer_id: offerId }
    );
  } catch (error) {
    console.error('Error declining proposal offer:', error);
    throw error;
  }
}

/**
 * Logs a proposal view event for repeat views (when status is already 'viewed' or other)
 * Use this to track subsequent views after the first one
 */
export async function logProposalView(
  proposalId: string,
  businessId: string,
  token: string,
  customerId: string,
  metadata: Record<string, any> = {}
) {
  if (!supabase) {
    console.error('Supabase client is not initialized');
    return null;
  }

  try {
    console.log(`Logging repeat view for proposal ${proposalId}`);
    
    return await logProposalEvent(
      proposalId,
      businessId,
      'viewed',
      'Proposal was viewed again by customer',
      {
        customer_id: customerId,
        token: token,
        user_agent: navigator.userAgent || 'unknown',
        timestamp: new Date().toISOString(),
        is_repeat_view: true,
        ...metadata
      }
    );
  } catch (error) {
    console.error('Error logging proposal view:', error);
    return null;
  }
}

/**
 * Cleans up duplicate proposal tokens, keeping only the most recent one per proposal
 */
export async function cleanupDuplicateTokens(proposalId?: string) {
  try {
    console.log('Cleaning up duplicate proposal tokens...');
    
    let query = supabase
      .from('public_proposal_tokens')
      .select('*')
      .order('proposal_id, created_at', { ascending: false });
    
    if (proposalId) {
      query = query.eq('proposal_id', proposalId);
    }

    const { data: allTokens, error: fetchError } = await query;

    if (fetchError) {
      console.error('Error fetching tokens for cleanup:', fetchError);
      return { success: false, error: fetchError.message };
    }

    if (!allTokens || allTokens.length === 0) {
      console.log('No tokens found for cleanup');
      return { success: true, deletedCount: 0 };
    }

    // Group tokens by proposal_id
    const tokensByProposal = allTokens.reduce((acc, token) => {
      if (!acc[token.proposal_id]) {
        acc[token.proposal_id] = [];
      }
      acc[token.proposal_id].push(token);
      return acc;
    }, {} as Record<string, any[]>);

    let deletedCount = 0;
    
    // For each proposal, keep only the most recent token and delete the rest
    for (const [proposalId, tokens] of Object.entries(tokensByProposal)) {
      if (tokens.length > 1) {
        // Keep the first one (most recent due to ordering), delete the rest
        const tokensToDelete = tokens.slice(1);
        const deleteIds = tokensToDelete.map(t => t.id);
        
        console.log(`Deleting ${deleteIds.length} duplicate tokens for proposal ${proposalId}`);
        
        const { error: deleteError } = await supabase
          .from('public_proposal_tokens')
          .delete()
          .in('id', deleteIds);
          
        if (deleteError) {
          console.error(`Error deleting duplicate tokens for proposal ${proposalId}:`, deleteError);
        } else {
          deletedCount += deleteIds.length;
          console.log(`Successfully deleted ${deleteIds.length} duplicate tokens for proposal ${proposalId}`);
        }
      }
    }

    console.log(`Cleanup complete. Deleted ${deletedCount} duplicate tokens.`);
    return { success: true, deletedCount };
  } catch (error) {
    console.error('Error in cleanupDuplicateTokens:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}

/**
 * Gets or creates a proposal token, with duplicate prevention
 */

